import { useState, useEffect, useCallback, useRef } from "react";
// Redux
import { useAppDispatch } from "./useStore";
// Async
import { getGoogleDriveConfig } from "app/store/Integrations/Integrations.async";
// Context
import { useUploadContext } from "app/context/UploadFiles";

const SCOPES = "https://www.googleapis.com/auth/drive.file";

const GOOGLE_EXPORT_MAP: Record<string, string> = {
  'application/vnd.google-apps.document': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.google-apps.spreadsheet': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.google-apps.presentation': 'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'application/vnd.google-apps.drawing': 'image/png', // PNG
};

const useGooglePicker = () => {
  const dispatch = useAppDispatch();

  const { onAdd, formState: { onSetIsGooglePickerLoading } } = useUploadContext();

  const [isReady, setIsReady] = useState(false);
  const [tokenClient, setTokenClient] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);

  const tokenExpiryRef = useRef<number>(0);

  useEffect(() => {
    let mounted = true;

    dispatch(getGoogleDriveConfig({})).unwrap()
      .then((config) => {
        if (mounted) {
          setApiKey(config.apiKey);
          setClientId(config.clientId);
          setAppId(config.appId);
        }
      })
      .catch((error) => {
        console.error("Failed to load Google Drive config:", error);
      });

    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) return resolve();
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });

    Promise.all([
      loadScript("https://apis.google.com/js/api.js"),
      loadScript("https://accounts.google.com/gsi/client"),
    ]).then(() => {
      (window as any).gapi.load("picker", () => mounted && setIsReady(true));
    }).catch(console.error);

    return () => { mounted = false };
  }, [dispatch]);

  const downloadGoogleFile = useCallback(async (
    fileId: string,
    fileName: string,
    token: string
  ): Promise<File> => {
    const metaRes = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=mimeType,name`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!metaRes.ok) {
      throw new Error(`Failed to fetch file metadata for file ${fileName} (ID: ${fileId})`);
    }
    const meta = await metaRes.json();
    const mimeType = meta.mimeType;
    const exportMime = GOOGLE_EXPORT_MAP[mimeType];

    const url = exportMime
      ? `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=${exportMime}`
      : `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;

    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Failed to download file ${fileName}`);

    const blob = await res.blob();
    const finalName = exportMime ? `${fileName}.${getExtensionByMimeType(exportMime)}` : fileName;
    return new File([blob], finalName, { type: blob.type });
    // eslint-disable-next-line
  }, []);

  const getExtensionByMimeType = useCallback((mimeType: string) => {
    switch (mimeType) {
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document": return "docx";
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": return "xlsx";
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation": return "pptx";
      case "image/png": return "png";
      default: return "bin";
    }
  }, []);

  const openPicker = useCallback((token?: string) => {
    if (!apiKey || !appId) return;
    const oauthToken = token || accessToken;
    if (!oauthToken) return;

    const { google } = window as any;
    const docsView = new google.picker.DocsView()
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMode(google.picker.DocsViewMode.LIST)

    const picker = new google.picker.PickerBuilder()
      .addView(docsView)
      .addView(new google.picker.DocsUploadView())
      .enableFeature(google.picker.Feature.NAV_HIDDEN)
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(oauthToken)
      // .setDeveloperKey(apiKey)
      .setAppId(appId)
      .setCallback(async (data: any) => {
        if (data.action === google.picker.Action.PICKED) {
          const files = data.docs;
          onSetIsGooglePickerLoading(true);
          try {
            const downloadedFiles = await Promise.all(files.map((f: any) =>
              downloadGoogleFile(f.id, f.name, oauthToken)
            ));
            onAdd(downloadedFiles);
          } catch (err) {
            console.error(err);
          } finally {
            onSetIsGooglePickerLoading(false);
          }
        }
      })
      .build();

    picker.setVisible(true);
  }, [accessToken, apiKey, appId, onAdd, onSetIsGooglePickerLoading, downloadGoogleFile]);

  useEffect(() => {
    if (!isReady || !clientId) return;

    const client = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: (tokenResponse: any) => {
        setAccessToken(tokenResponse.access_token);
        tokenExpiryRef.current = Date.now() + (tokenResponse.expires_in * 1000) - 30000; // Subtract 5 minutes for safety
        openPicker(tokenResponse.access_token);
      },
    });

    setTokenClient(client);
    // eslint-disable-next-line
  }, [isReady, clientId]);

  const handleOpenPicker = useCallback(() => {
    if (!tokenClient) return;

    const isTokenExpired = !accessToken || Date.now() >= tokenExpiryRef.current;
    if (isTokenExpired) {
      tokenClient.requestAccessToken({ prompt: "" });
    } else {
      openPicker();
    }
  }, [tokenClient, accessToken, openPicker]);

  return {
    isReady,
    isAuthorized: Boolean(accessToken),
    openPicker: handleOpenPicker
  };
};

export default useGooglePicker;
